import { type ReactNode, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { tv, type VariantProps } from "tailwind-variants";

import type { Icons } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn, type VariantSlotsToClassNames } from "@virtual-brain-lib/frontend-design-system/utils";
import type { MessageToolStatus } from "@virtual-brain-lib/types";

import { useToolCardContext } from "./ToolCardContext";
import { ToolCardHeader } from "./ToolCardHeader";
import { ToolCardStatusText } from "./ToolCardStatusText";

export const toolCardVariants = tv({
	slots: {
		base: "relative flex w-full max-w-125 flex-col overflow-hidden rounded-lg border border-secondary-border bg-background",
		headerWrapper: "transition-[padding] duration-200 ease-out",
		body: "flex flex-col gap-2",
		bodyWrapper: "flex flex-col gap-2.5 px-3 pb-2.5",
	},
	variants: {
		status: {
			processing: { base: "shadow-sm" },
			processed: { base: "" },
		},
		isCompact: {
			true: { headerWrapper: "px-3 py-1.5" },
			false: { headerWrapper: "px-3 pt-2.5 pb-0" },
		},
		isClickable: {
			true: {
				headerWrapper:
					"block w-full cursor-pointer text-left transition-colors hover:bg-secondary-hover",
			},
			false: {},
		},
	},
	defaultVariants: {
		status: "processing",
		isCompact: false,
		isClickable: false,
	},
});

export type ToolCardVariant = VariantProps<typeof toolCardVariants>;

export interface ToolCardProps {
	readonly icon: Icons;
	readonly title: string;
	readonly status: MessageToolStatus;
	readonly statusText?: string;
	readonly compactSummary?: string;
	readonly statusIcon?: Icons;
	readonly className?: string;
	readonly classNames?: VariantSlotsToClassNames<typeof toolCardVariants>;
	readonly children?: ReactNode;
}

export function ToolCard({
	icon,
	title,
	status,
	statusText,
	compactSummary,
	statusIcon,
	className,
	classNames,
	children,
}: ToolCardProps) {
	const { mode } = useToolCardContext();
	const isCompact = mode === "compact";
	const [isExpanded, setIsExpanded] = useState(false);

	const { base, headerWrapper, bodyWrapper, body } = toolCardVariants({
		status,
		isCompact,
		isClickable: isCompact,
	});

	const getTrailingIcon = (): Icons | undefined => {
		if (!isCompact) {
			return undefined;
		}

		return isExpanded ? "chevronUp" : "chevronDown";
	};

	const headerContent = (
		<ToolCardHeader
			icon={icon}
			status={status}
			title={title}
			subtitle={isCompact ? compactSummary : undefined}
			trailingIcon={getTrailingIcon()}
			statusIcon={statusIcon}
		/>
	);

	const bodyContent = (statusText || children) && (
		<div className={bodyWrapper()}>
			{statusText && <ToolCardStatusText statusText={statusText} />}
			{children && <div className={body({ className: classNames?.body })}>{children}</div>}
		</div>
	);

	return (
		<div className={base({ className: cn(className, classNames?.base) })}>
			{isCompact ? (
				<button
					type="button"
					className={headerWrapper()}
					aria-expanded={isExpanded}
					onClick={() => {
						setIsExpanded((prev) => !prev);
					}}
				>
					{headerContent}
				</button>
			) : (
				<div className={headerWrapper()}>{headerContent}</div>
			)}
			{isCompact ? (
				<AnimatePresence initial={false}>
					{isExpanded && (
						<motion.div
							key="expanded"
							className="overflow-hidden"
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: "auto", opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
							transition={{ duration: 0.2, ease: "easeOut" }}
						>
							{bodyContent}
						</motion.div>
					)}
				</AnimatePresence>
			) : (
				bodyContent
			)}
		</div>
	);
}
